/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useState, useMemo, useEffect } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";
import localforage from "localforage";
import { useAppServices } from "hook/services";
import Loader from "examples/Loader";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import RemoveAccessModal from "./RemoveAccessModal";
import Maintainance from "components/Maintainance/Maintainance";
import ThemeTypeModel from "./ThemeTypeModel";
// import env from "../config";
// import {
//   setDarkMode,
//   setFixedNavbar,
//   setSidenavColor,
//   setTransparentSidenav,
//   setWhiteSidenav,
//   useMaterialUIController,
// } from "context";

// Material Dashboard 2 React main context
const AgencyContext = createContext();

// Setting custom name for the context which is visible on react dev tools
AgencyContext.displayName = "AgencyContext";

// Material Dashboard 2 React context provider
function AgencyProvider({ children }) {
  const Service = useAppServices();
  const [agency, setAgency] = useState({});
  const [agencyConfig, setAgencyConfig] = useState({});
  const [loader, setLoader] = useState(true);
  const [model, setModel] = useState(false);
  const [openthemeModel, setOpenThemeModel] = useState(false);
  const [activities, setActivities] = useState([]);
  const [accessModel, SetAccessModel] = useState(false);
  //  const [eventLoading, setEventLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const Update = (updates) => {
    const data = { ...agency, ...updates };
    setAgency(data);
  };

  const UpdateAgencyConfig = (updates) => {
    const data = { ...agencyConfig, ...updates };

    setAgencyConfig(data);
    localforage.setItem("agency_config", data);
  };

  const clear = () => setAgency({});

  const value = useMemo(
    () => [agency, Update, clear, agencyConfig, UpdateAgencyConfig, activities], // eventLoading //
    [agency, Update, clear, agencyConfig, UpdateAgencyConfig, activities] // eventLoading //
  );

  const getAgency = async (localAgency) => {
    const token = await localforage.getItem("token");
    const { response } = await Service.agency.get({
      query: `_id=${localAgency._id}`,
      token,
    });
    if (!response || !response.data) {
      setNotFound(true);
      return setLoader(false);
    }
    if (!response.data?.privacy_policy?.checked) {
      setModel(true);
    }
    if(response.data?.privacy_policy?.checked && !response?.data?.theme_id){
        setOpenThemeModel(true);
    }
    if (!response.data?.access) {
      SetAccessModel(true);
    }
    setAgency({ ...response.data });
    //  GetEventData(response?.data?.ghl?.location_id);
    localforage.setItem("agency", { ...response.data });
    setAgencyConfig({ ...response.agency_config });
    localforage.setItem("agency_config", { ...response.agency_config });
    setActivities(response?.activities);
    return setLoader(false);
  };

  const updateAgency = async () => {
    const localAgency = await localforage.getItem("agency");
    if (!localAgency) return setLoader(false);
    setAgency({ ...localAgency });
    setLoader(false);
    return getAgency(localAgency);
  };

  // const GetEventData = async (id) => {
  //   try {
  //     const { response } = await Service.FetchEventData.Get({
  //       query: `location_id=${id}`,
  //     });
  //     if (response?.success) {
  //       console.log("Data fetched successfully");
  //     }
  //   } catch (error) {
  //     console.warn("Event fetch failed", error);
  //   } finally {
  //     setEventLoading(false);
  //   }
  // };

  const onLoad = () => {
    updateAgency();
  };

  useEffect(onLoad, []);

  if (notFound) {
    return <Maintainance message="Agency not found or currently under maintenance." />;
  }

  return loader ? (
    <Loader />
  ) : (
    <AgencyContext.Provider value={value}>
      {model && (
        <PrivacyPolicyModal
          open={model}
          onClose={() => setModel(false)}
          Update={Update}
          agency={agency}
          openThemeSelect={() => setOpenThemeModel(true)}
        />
      )}
      {openthemeModel && (
        <ThemeTypeModel
          open={openthemeModel}
          onClose={() => setOpenThemeModel(false)}
          Update={Update}
          agency={agency}
        />
      )}
      {accessModel && (
        <RemoveAccessModal
          open={accessModel}
          onClose={() => SetAccessModel(false)}
          Update={Update}
          agency={agency}
        />
      )}
      {children}
    </AgencyContext.Provider>
  );
}

// Material Dashboard 2 React custom hook for using context
function useAgencyInfo() {
  return useContext(AgencyContext) || [];
}

// Typechecking props for the MaterialUIControllerProvider
AgencyProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { AgencyProvider, useAgencyInfo };
